import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Button from 'components/ui-components/Button';
import { useHTTP } from 'hooks/useRequest';
import { HookFormProvider } from 'components/cef_form/hook-form/providers/HookFormProvider';
import HookFormTextInput from 'components/cef_form/hook-form/HookFormTextInput';
import HookFormSelect from 'components/cef_form/hook-form/HookFormSelect';
import { formTitles } from 'constants/RegistrationForm/constants';

import AlertBox from 'components/ui-components/AlertBox';
import CheckBox from 'components/ui-components/CheckBox';
import GoogleRecaptcha from 'components/reCAPTCHA/GoogleRecaptcha';
import Card from 'components/ui-components/Card';
import LoadingSpinner from 'components/ui-components/loaders/LoadingSpinner';
import ContactOptions from 'components/mycef/Register/RegisterForm/Summary/ContactOptions';

const ActivateForm = ({ recaptchaKeys, bypassRecaptcha }) => {
  const [contactOptions, setContactOptions] = useState({
    marketing_opt_in: '1',
    direct_mail_communication: '0',
    email_communication: '0',
    sms_communication: '0',
    phone_communication: '0',
  });

  const [termsAndConditions, setTermsAndConditions] = useState('0');
  const [termsError, setTermsError] = useState(false);
  const [v2Key, v3Key] = recaptchaKeys;
  const [token, setToken] = useState();
  const [loading, setLoading] = useState(false);
  const [updatedForm, setUpdatedForm] = useState({});

  const [response, setResponse] = useState({ status: undefined, data: {} });
  const [formError, setFormError] = useState('');
  const [clickedActivatedAccount, setClickedActivatedAccount] = useState(false);
  const [updatedFormState, setUpdatedFormState] = useState({});

  const {
    control,
    handleSubmit,
    watch,
    setError,
    setValue,
    trigger,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: '',
      forename: '',
      surname: '',
      email: '',
      account_number: '',
      postcode: '',
      marketing_opt_in: '1',
      direct_mail_communication: '0',
      email_communication: '0',
      sms_communication: '0',
      phone_communication: '0',
      terms_and_conditions: false,
    },
  });

  const titlePlaceholder = 'Select';
  const titleError = 'Please select a title';

  const navigate = useNavigate();

  const requestConfig = {
    url: token ? '/mycef/account' : null,
    method: 'post',
    data: { ...updatedForm, 'g-recaptcha-response': token },
  };

  const {
    error: err,
    data: res,
    loading: loadingData,
  } = useHTTP(requestConfig);

  useEffect(() => {
    setLoading(loadingData);
  }, [loadingData]);

  useEffect(() => {
    // Need to reset clickedCreateAccount so recaptchas submit useEffect can run
    if (clickedActivatedAccount) {
      setTimeout(() => {
        setClickedActivatedAccount(false);
      }, 0);
    }
  }, [clickedActivatedAccount]);

  useEffect(() => {
    setToken('');
    if (res) {
      // a successful res will return an object
      if (typeof res === 'object') {
        setResponse({ status: 200, data: res });
        // a failed res will return an array
      } else if (res.length >= 1) {
        // successful call and issue with data return error
        setResponse({
          status: res[1].status,
          data: res[0].error,
        });
      }
    }
  }, [res]);

  useEffect(() => {
    setToken('');

    if (typeof err === 'undefined') return;

    const { error } = err || {};
    const { status, data, statusText } = error || {};

    setClickedActivatedAccount(false);

    // Handle specific error statuses
    if (status === 400) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setResponse({
        status,
        data: data?.error || 'recaptcha failed',
        responseType: data?.response_type,
      });
    } else if (status === 404 || status === 503) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setResponse({
        status,
        data: statusText,
      });
    }
  }, [err]);

  useEffect(() => {
    setClickedActivatedAccount(false);

    const { status, data, responseType, error } = response;

    if (status === 200) {
      navigate('/mycef/account/new/activation-success', {
        state: updatedForm,
      });
    } else if (status === 403 || status === 406) {
      setClickedActivatedAccount(false);
    } else if (status === 400 && data !== 'recaptcha failed') {
      // Scroll to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (typeof data === 'object') {
        // Set backend error messages to form fields
        Object.entries(data).forEach(([fieldName, errorMessages]) => {
          /**
           * Maps the backend key account_id to the account_number field otherwise just use key.
           * For example, `account_id` is mapped to `account_number` due to how the backend creates errors.
           */
          const formFieldName =
            fieldName === 'account_id' ? 'account_number' : fieldName;

          setError(formFieldName, {
            type: 'backend',
            message: errorMessages[0],
          });
        });
      } else if (responseType === 'service_error' && error === 'unsuccessful') {
        setClickedActivatedAccount(false);
      } else {
        setError(data);
      }
    }

    setClickedActivatedAccount(false);
  }, [response]);

  useEffect(() => {
    const subscription = watch(values => {
      setUpdatedFormState({
        ...values,
      });
    });

    setUpdatedForm({
      account: 'true',
      customer: {
        ...updatedFormState,
        ...contactOptions,
        terms_and_conditions: termsAndConditions,
      },
    });
    return () => subscription.unsubscribe();
  }, [watch, updatedFormState, contactOptions, termsAndConditions]);

  const handleFormSubmit = () => {
    setFormError('');

    if (termsAndConditions === '1') {
      setTermsError(false);
      setClickedActivatedAccount(true);
    } else if (termsAndConditions === '0') {
      setTermsError(true);
    }
  };

  return (
    <HookFormProvider
      control={control}
      trigger={trigger}
      watch={watch}
      setValue={setValue}
      errors={errors}
      setError={setError}
      reset={reset}
      clearErrors={clearErrors}
      // Pass unique values to the provider for this form only - allows us to keep it generic
      options={{
        recaptchaKeys,
        bypassRecaptcha,
      }}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <h2 className="ta-c ff-body fw-700 fs-30 s-fs-34 m-fs-36 lh-1 tc-main">
          Register your Account
        </h2>
        <div className="fl jc-c">
          <Card
            variant="large"
            className="register-form__card m-fb-70 l-fb-35 pos-r ov-hidden">
            {formError && (
              <AlertBox
                className="plr-13 pt-10 pb-13 mb-12 summary-submission-error"
                variant="error"
                iconHeight="20"
                body={formError}
                bodyClass="fs-14 tc-cef-red ff-body"
              />
            )}
            <HookFormSelect
              id="title"
              label="Title"
              selectItems={formTitles}
              rules={{
                required: titleError,
              }}
              className="mt-20 width-40"
              placeholder={titlePlaceholder}
            />

            <HookFormTextInput
              className="mt-20"
              id="forename"
              type="text"
              label="First Name"
              rules={{
                required: 'Please enter your first name',
              }}
            />

            <HookFormTextInput
              className="mt-20"
              id="surname"
              type="text"
              label="Last Name"
              rules={{
                required: 'Please enter your last name',
              }}
            />

            <HookFormTextInput
              className="mt-20"
              id="email"
              type="email"
              label="Email"
              rules={{
                required: 'Please enter your email address',
              }}
            />

            <div className="form__line-break" />

            <HookFormTextInput
              description="This is the 8 digit number on your invoice"
              className="mt-20"
              id="account_number"
              type="text"
              label="Account Number"
              rules={{
                required: 'Please enter your account number',
              }}
            />

            <HookFormTextInput
              description="Please ensure this matches your invoice address"
              className="mt-20 mb-36 existing-account-registration__postcode-input"
              id="postcode"
              type="text"
              label="Postcode"
              rules={{
                required: 'Please enter your postcode',
              }}
            />

            <ContactOptions
              contactOptions={contactOptions}
              setContactOptions={e => setContactOptions(e)}
            />

            <CheckBox
              key="termsAndConditions"
              id="termsAndConditions"
              value={termsAndConditions !== '0'}
              onChange={() =>
                setTermsAndConditions(curState =>
                  curState === '0' ? '1' : '0',
                )
              }
              label='I have read and accept the <a href="/info/terms_and_conditions" target="_blank" rel="noreferrer">Terms and Conditions</a>'
              className="fl mt-14 mb-3"
              labelClass="fw-400"
              error={termsError}
              errorMsg="Please confirm you have read the Terms and Conditions"
            />

            <GoogleRecaptcha
              res={res}
              error={err}
              submit={clickedActivatedAccount}
              bypassRecaptcha={bypassRecaptcha}
              action="create"
              siteKeyV3={v3Key}
              siteKeyV2={v2Key}
              setToken={setToken}
              className="mt-8"
              v2Error="Please complete the reCaptcha above"
              v2ErrorClass="cef-ui-input-error cef-ui-input__error mb-0"
            />

            <div className="mt-32">
              <Button
                className="cef-btn cef-btn--feature cef-btn--green width-100 m-width-auto cef-btn--xxl mt-24"
                type="submit"
                disabled={loading}>
                {loading ? <LoadingSpinner /> : 'Register Account'}
              </Button>
            </div>
          </Card>
        </div>
      </form>
    </HookFormProvider>
  );
};

export default ActivateForm;
